import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, ArrowRight, RotateCcw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtDate, INPUT_LIGHT } from "@/lib/constants";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { JobForm, StatusBadge } from "./JobForm";
import { Overview } from "./Overview";
import { AtsBoard } from "./AtsBoard";
import { CrmPanel } from "./CrmPanel";

const tabTriggerCls = "text-slate-500 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm";

const SectionError = ({ label, onRetry }) => (
  <div className="rounded-2xl border border-rose-200 bg-rose-50/60 p-10 text-center" data-testid={`section-error-${label}`}>
    <p className="text-sm text-slate-500">This section couldn&rsquo;t load — the rest of your dashboard is unaffected.</p>
    <button
      onClick={onRetry}
      data-testid={`section-retry-${label}`}
      className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2 text-xs font-medium text-slate-600 transition-colors duration-200 hover:border-brand/60 hover:text-brand"
    >
      <RotateCcw size={13} /> Retry
    </button>
  </div>
);

export const AdminWorkspace = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [deals, setDeals] = useState([]);
  const [errors, setErrors] = useState({});
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [notifEmail, setNotifEmail] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);

  // Each section loads independently — one failed request can never blank the whole workspace.
  const loaders = {
    core: async () => {
      const [s, a] = await Promise.all([api.get("/stats"), api.get("/applications")]);
      setStats(s.data);
      setApplications(a.data);
    },
    jobs: async () => {
      const { data } = await api.get("/jobs?all=1");
      setJobs(data);
    },
    enquiries: async () => {
      const { data } = await api.get("/enquiries");
      setEnquiries(data);
    },
    crm: async () => {
      const [c, d] = await Promise.all([api.get("/crm/companies"), api.get("/crm/deals")]);
      setCompanies(c.data);
      setDeals(d.data);
    },
    settings: async () => {
      const { data } = await api.get("/settings");
      setNotifEmail(data.notification_email || "");
    },
  };

  const refresh = useCallback(async (key) => {
    setErrors((e) => ({ ...e, [key]: false }));
    try {
      await loaders[key]();
    } catch {
      setErrors((e) => ({ ...e, [key]: true }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    Object.keys(loaders).forEach((k) => refresh(k));
  }, [refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshJobsAndCore = () => { refresh("jobs"); refresh("core"); };

  const deleteJob = async (job) => {
    if (!window.confirm(`Delete "${job.title}"? Applications for it will also be removed.`)) return;
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success("Role deleted.");
      refreshJobsAndCore();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/settings", { notification_email: notifEmail });
      setNotifEmail(data.notification_email);
      toast.success("Notification email saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingSettings(false);
    }
  };

  const convertLead = async (enquiry) => {
    try {
      await api.post(`/enquiries/${enquiry.id}/convert-lead`);
      toast.success(`${enquiry.company || enquiry.name} added to CRM as a lead.`);
      refresh("enquiries");
      refresh("crm");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-workspace">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-brand">Command center</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-slate-900" data-testid="dashboard-greeting">
            Hello, Samanvay HR admin.
          </h1>
        </div>
        <button
          onClick={() => { setEditing(null); setShowForm(true); }}
          data-testid="post-job-btn"
          className="flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-medium text-white transition-all duration-300 hover:bg-[#E63E12] hover:shadow-glow"
        >
          <Plus size={16} /> Post a role
        </button>
      </div>

      <div className="mt-10 rounded-[28px] bg-[#F5F6F8] p-4 sm:p-6 lg:p-8 border border-slate-200/80" data-testid="admin-light-shell">
        {showForm && (
          <div className="mb-8">
            <JobForm
              light
              initial={editing}
              onSaved={() => { setShowForm(false); setEditing(null); refreshJobsAndCore(); }}
              onCancel={() => { setShowForm(false); setEditing(null); }}
            />
          </div>
        )}

        <Tabs defaultValue="overview">
          <TabsList className="bg-slate-200/70 flex-wrap h-auto" data-testid="dashboard-tabs">
            <TabsTrigger value="overview" className={tabTriggerCls} data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="ats" className={tabTriggerCls} data-testid="tab-ats">ATS Pipeline ({applications.length})</TabsTrigger>
            <TabsTrigger value="crm" className={tabTriggerCls} data-testid="tab-crm">CRM ({companies.length})</TabsTrigger>
            <TabsTrigger value="jobs" className={tabTriggerCls} data-testid="tab-jobs">Roles ({jobs.length})</TabsTrigger>
            <TabsTrigger value="enquiries" className={tabTriggerCls} data-testid="tab-enquiries">Enquiries ({enquiries.length})</TabsTrigger>
            <TabsTrigger value="settings" className={tabTriggerCls} data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-8" data-testid="overview-panel">
            {errors.core ? (
              <SectionError label="overview" onRetry={() => refresh("core")} />
            ) : (
              <Overview stats={stats} applications={applications} deals={deals} />
            )}
          </TabsContent>

          <TabsContent value="ats" className="mt-8" data-testid="ats-panel">
            {errors.core ? (
              <SectionError label="ats" onRetry={() => refresh("core")} />
            ) : (
              <AtsBoard applications={applications} onRefresh={() => refresh("core")} />
            )}
          </TabsContent>

          <TabsContent value="crm" className="mt-8" data-testid="crm-tab-panel">
            {errors.crm ? (
              <SectionError label="crm" onRetry={() => refresh("crm")} />
            ) : (
              <CrmPanel companies={companies} deals={deals} onRefresh={() => refresh("crm")} />
            )}
          </TabsContent>

          <TabsContent value="jobs" className="mt-8" data-testid="jobs-panel">
            {errors.jobs ? (
              <SectionError label="jobs" onRetry={() => refresh("jobs")} />
            ) : jobs.length === 0 ? (
              <p className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-400" data-testid="no-jobs">
                No roles posted yet.
              </p>
            ) : (
              <div className="space-y-4">
                {jobs.map((j) => (
                  <div key={j.id} className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-wrap items-center justify-between gap-4" data-testid={`job-row-${j.id}`}>
                    <div>
                      <div className="flex items-center gap-3">
                        <p className="font-display text-lg font-semibold text-slate-900">{j.title}</p>
                        <StatusBadge status={j.status} light />
                      </div>
                      <p className="mt-1 text-sm text-slate-500">{j.company} · {j.location} · {j.type}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setEditing(j); setShowForm(true); window.scrollTo(0, 0); }}
                        data-testid={`edit-job-${j.id}`}
                        className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs text-slate-600 hover:border-brand/60 hover:text-brand transition-colors duration-200"
                      >
                        <Pencil size={13} /> Edit
                      </button>
                      <button
                        onClick={() => deleteJob(j)}
                        data-testid={`delete-job-${j.id}`}
                        className="flex items-center gap-2 rounded-full border border-rose-200 px-4 py-2 text-xs text-rose-500 hover:border-rose-400 transition-colors duration-200"
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="enquiries" className="mt-8" data-testid="enquiries-panel">
            {errors.enquiries ? (
              <SectionError label="enquiries" onRetry={() => refresh("enquiries")} />
            ) : enquiries.length === 0 ? (
              <p className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-400" data-testid="no-enquiries">
                No enquiries yet.
              </p>
            ) : (
              <div className="space-y-4">
                {enquiries.map((e) => (
                  <div key={e.id} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`enquiry-row-${e.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-baseline gap-2">
                          <p className="font-display text-lg font-semibold text-slate-900">{e.name}</p>
                          {e.company && <span className="text-slate-500 text-sm">· {e.company}</span>}
                          {e.converted_company_id && (
                            <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-600 uppercase tracking-[0.15em] text-[10px]" data-testid={`enquiry-converted-${e.id}`}>
                              In CRM
                            </Badge>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-brand">{e.email}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="text-xs text-slate-400">{fmtDate(e.created_at)}</p>
                        {!e.converted_company_id && (
                          <button
                            onClick={() => convertLead(e)}
                            data-testid={`enquiry-convert-${e.id}`}
                            className="flex items-center gap-2 rounded-full border border-brand/40 px-4 py-2 text-xs text-brand transition-colors duration-200 hover:bg-orange-50"
                          >
                            Convert to lead <ArrowRight size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="mt-3 text-sm text-slate-500 leading-relaxed">{e.message}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="settings" className="mt-8" data-testid="settings-panel">
            {errors.settings ? (
              <SectionError label="settings" onRetry={() => refresh("settings")} />
            ) : (
              <div className="rounded-2xl border border-brand/30 bg-orange-50/60 p-7 flex flex-wrap items-end gap-4" data-testid="admin-settings">
                <div className="flex-1 min-w-[260px] space-y-2">
                  <Label htmlFor="settings-email" className="text-slate-600">
                    Notification email — every new application & enquiry is emailed here
                  </Label>
                  <Input
                    id="settings-email"
                    type="email"
                    value={notifEmail}
                    onChange={(e) => setNotifEmail(e.target.value)}
                    data-testid="settings-email-input"
                    className={INPUT_LIGHT}
                    placeholder="you@yourcompany.com"
                  />
                </div>
                <button
                  onClick={saveSettings}
                  disabled={savingSettings || !notifEmail}
                  data-testid="settings-save-btn"
                  className="rounded-full bg-brand px-7 py-3 text-sm font-medium text-white transition-shadow duration-300 hover:shadow-glow disabled:opacity-50"
                >
                  {savingSettings ? "Saving..." : "Save"}
                </button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
